import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, GitMerge, Search, XCircle } from "lucide-react";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { formatDateTime } from "../lib/format";

function baitLabel(row: any) {
  return [row.brand, row.product_name].filter(Boolean).join(" ") || "Unnamed bait";
}

export default function AdminBaitReviewsPage() {
  const [workingId, setWorkingId] = useState<number | null>(null);
  const [linkingId, setLinkingId] = useState<number | null>(null);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [message, setMessage] = useState("");

  const queue = useQuery({
    queryKey: ["admin_bait_reviews"],
    queryFn: () =>
      fetch("/app-api/admin/bait-reviews").then(async (r) => {
        if (!r.ok) throw new Error("Could not load bait reviews");
        return r.json();
      }),
  });

  async function act(id: number, action: "approve" | "reject", body?: any) {
    setWorkingId(id);
    setMessage("");
    try {
      const response = await fetch(`/app-api/admin/bait-reviews/${id}/${action}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: body ? JSON.stringify(body) : undefined,
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Could not review bait");
      await queue.refetch();
      setLinkingId(null);
      setSearchQ("");
      setSearchResults([]);
      setMessage(action === "approve" ? "Bait approved and added to the shared list." : "Bait suggestion rejected.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not review bait");
    } finally {
      setWorkingId(null);
    }
  }

  async function searchExisting() {
    if (searchQ.trim().length < 2) return;
    setSearching(true);
    try {
      const response = await fetch(`/app-api/baits/search?q=${encodeURIComponent(searchQ.trim())}`);
      setSearchResults(response.ok ? await response.json() : []);
    } finally {
      setSearching(false);
    }
  }

  async function linkExisting(id: number, canonicalId: string) {
    setWorkingId(id);
    setMessage("");
    try {
      const response = await fetch(`/app-api/admin/bait-reviews/${id}/link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ canonical_id: canonicalId }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Could not link bait");
      await queue.refetch();
      setLinkingId(null);
      setSearchQ("");
      setSearchResults([]);
      setMessage("Suggestion linked to the existing bait. No duplicate was created.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not link bait");
    } finally {
      setWorkingId(null);
    }
  }

  const reviews: any[] = queue.data ?? [];

  return (
    <div className="mx-auto max-w-[1000px] space-y-6 px-4 py-6 pb-28">
      <header>
        <Link to="/admin" className="text-sm font-medium text-accent hover:underline">← Admin</Link>
        <h1 className="mt-2 text-2xl font-semibold text-primary">Bait reviews</h1>
        <p className="mt-1 text-sm leading-6 text-secondary">
          Anglers can use a missing bait immediately on their own account. Review it here to approve it for everyone, link it to an existing duplicate, or reject it.
        </p>
      </header>

      {message ? (
        <p className="rounded-lg border border-border bg-raised p-3 text-sm text-secondary" role="status">{message}</p>
      ) : null}

      {queue.isLoading ? (
        <p className="rounded-lg border border-border bg-raised p-5 text-sm text-secondary">Loading bait submissions…</p>
      ) : queue.error ? (
        <p className="rounded-lg border border-error bg-error-weak p-5 text-sm text-error">Could not load bait submissions.</p>
      ) : reviews.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-secondary">No pending bait reviews.</div>
      ) : (
        <div className="space-y-3">
          {reviews.map((review) => (
            <section key={review.id} className="rounded-xl border border-border bg-raised p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="font-semibold text-primary">{baitLabel(review)}</h2>
                  <p className="mt-1 text-sm text-secondary">
                    {[review.bait_type?.replaceAll("_", " "), review.presentation_role, review.species_applicability]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                  <p className="mt-1 text-xs text-secondary">
                    Submitted by {review.submitted_by_name || review.submitted_by_email || "angler"} · {formatDateTime(review.created_at)}
                  </p>
                </div>
                <span className="rounded-full bg-warning-weak px-2 py-1 text-xs font-semibold text-warning">Pending</span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <Button size="sm" onClick={() => act(Number(review.id), "approve")} isLoading={workingId === Number(review.id)}>
                  <CheckCircle2 className="size-4" /> Approve new bait
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setLinkingId(linkingId === Number(review.id) ? null : Number(review.id));
                    setSearchQ(review.product_name || "");
                    setSearchResults([]);
                  }}
                >
                  <GitMerge className="size-4" /> Link duplicate
                </Button>
                <Button size="sm" variant="tertiary" onClick={() => act(Number(review.id), "reject")} isLoading={workingId === Number(review.id)}>
                  <XCircle className="size-4" /> Reject
                </Button>
              </div>

              {linkingId === Number(review.id) ? (
                <div className="mt-4 space-y-3 rounded-lg border border-border bg-background p-3">
                  <p className="text-sm font-semibold text-primary">Find the existing bait</p>
                  <div className="flex gap-2">
                    <Input
                      value={searchQ}
                      onChange={(event) => setSearchQ(event.target.value)}
                      onKeyDown={(event) => event.key === "Enter" && searchExisting()}
                      placeholder="Brand or bait name"
                    />
                    <Button type="button" variant="secondary" onClick={searchExisting} isLoading={searching}>
                      <Search className="size-4" /> Search
                    </Button>
                  </div>
                  {searchResults.length > 0 ? (
                    <div className="divide-y divide-border rounded-lg border border-border bg-raised">
                      {searchResults.map((bait) => (
                        <button
                          key={bait.id}
                          type="button"
                          onClick={() => linkExisting(Number(review.id), bait.id)}
                          className="flex w-full items-center justify-between gap-3 p-3 text-left hover:bg-inset"
                        >
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-semibold text-primary">
                              {[bait.brand !== "Generic" ? bait.brand : "", bait.product_name].filter(Boolean).join(" ")}
                            </span>
                            <span className="block text-xs text-secondary">{String(bait.bait_type || "").replaceAll("_", " ")}</span>
                          </span>
                          <GitMerge className="size-4 shrink-0 text-accent" />
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
